export enum SectionWithAccordion {
  LIBRARIES = `LIBRARIES`,
  CLOUD_SOURCES = `CLOUD_SOURCES`,
  SOURCE_FUNCTIONS = `SOURCE_FUNCTIONS`,
  API = `API`,
  VALIDATION_AND_TRANSFORMATION = `VALIDATION_AND_TRANSFORMATION`,
  DEDUPLICATION = `DEDUPLICATION`,
  GDPR_SUPPRESSION = `GDPR_SUPPRESSION`,
  IDENTITY_RESOLUTION = `IDENTITY_RESOLUTION`,
  PROFILE_API = `PROFILE_API`,
  REVERSE_ETL = `REVERSE_ETL`,
  AUDIENCE_CREATION = `AUDIENCE_CREATION`,
  CENTRIFUGE = `CENTRIFUGE`,
  DESTINATIONS = `DESTINATIONS`,
  WAREHOUSES_ETL = `WAREHOUSES_ETL`,
}

interface AccordionData {
  title: string
  description: string
}

interface SectionData {
  [key: string]: AccordionData[]
}

const accordionData: SectionData = {
  [SectionWithAccordion.LIBRARIES]: [
    {
      title: `Meet you in your language`,
      description: `No one likes having to fiddle around with REST APIs. We’ve built libraries in 12 major languages to get you up and running, quickly.`,
    },
    {
      title: `Batching`,
      description: `Each batch of requests is gzip compressed, decreasing the bytes on the wire by 10-20x. Thanks to this batching and compression, our SDKs reduce energy overhead by 2-3x. This means longer battery life for your users.`,
    },
    {
      title: `Edge persistence`,
      description: `JavaScript, iOS, and Android all locally queue data to make sure you don’t miss events. Each library uses fast, <a href="https://segment.com/blog/queuefile-reliable-request-batching-android/">atomic transactions to ensure fidelity.</a>`,
    },
    {
      title: `Automatic retries`,
      description: `If there’s a network failure due to a connection reset, timeout, or DNS error, our SDKs retry until the request is successful. This improves data deliverability by 0.6%, meaning that you never miss an event.`,
    },
  ],
  [SectionWithAccordion.CLOUD_SOURCES]: [
    {
      title: `Dynamically handle rate limits`,
      description: `Salesforce, Stripe, and Zendesk dynamically rate limit from 60 requests per minute to 1,000 per hour. We automatically handle these limits and backoff to get fast data without triggering resets.`,
    },
    {
      title: `Incremental syncs`,
      description: `You shouldn’t need to pull all data, every time. If the API supports it, we’ll keep historically checkpoints and incrementally sync just the delta. This speeds sync times by 60-100x.`,
    },
    {
      title: `Merging objects`,
      description: `Some APIs will only give back changes in individual fields. You don’t have to materialize all of these changes yourself, we’ll nicely materialize these deltas into a single “merged” object.`,
    },
  ],
  [SectionWithAccordion.SOURCE_FUNCTIONS]: [
    {
      title: `Write 20 lines of javascript`,
      description: `You don’t have to worry about setting up a server, terminating connections, or other configuration. We handle all of the plumbing, so you write your business logic and go.`,
    },
    {
      title: `Scaling up, concurrency, and dead letter queues`,
      description: `We’ll automatically scale functions up and down, as well as handle their concurrency. Any data which is discarded is automatically published to a Dead Letter Queue for later processing.`,
    },
    {
      title: `Autocompletion and testing in-browser`,
      description: `No need to copy and paste little bits of JSON. Sample one live request, and then let autocomplete handle all of the rest.`,
    },
  ],
  [SectionWithAccordion.API]: [
    {
      title: `Scaled up 10x for 99.9999% availability`,
      description: `We have our servers intentionally scaled down at the edge. It allows us to ingest events even with news spikes or live events.`,
    },
    {
      title: `High-performance Go servers`,
      description: `Our Tracking API is written as a high-performance golang service. Each instance can handle tens of thousands of requests concurrently. We’re collecting more than 800,000 RPS.`,
    },
    {
      title: `Queued locally`,
      description: `We queue all data at the edge locally first. In case of a network partition, we can continue processing.`,
    },
    {
      title: `Stateless at the edge`,
      description: `None of our tracking servers require configuration data. Under times of more load, we can bring up new servers without degrading our QoS.`,
    },
  ],
  [SectionWithAccordion.VALIDATION_AND_TRANSFORMATION]: [
    {
      title: `Fast, nested JSON parsing`,
      description: `It’s not easy to parse and match thousands of requests per second flowing. We’ve <a href="https://github.com/segmentio/encoding">built our own custom JSON parser which does zero-memory allocations</a>, and optimized regex parsers to ensure that your data keeps moving.`,
    },
    {
      title: `Versioned transformations`,
      description: `It’s simple to build a basic transformation worker… but what happens when you have 20 different versions of mobile apps out in the wild, each using different tracking? Our transformations and filters are versioned from the start to let you apply your controls with fine-grained matchers.`,
    },
  ],
  [SectionWithAccordion.DEDUPLICATION]: [
    {
      title: `Exactly-once guarantees with Kafka`,
      description: `We’ve partitioned Kafka based upon the ID of each message. Whenever a deduplication worker starts, it first reads the Kafka output topic as the “source of truth” before processing messages.`,
    },
    {
      title: `Sub-millisecond queries with RocksDB`,
      description: `Each dedupe instance queries from a RocksDB instance, which is replicated via EBS. If a database ever goes missing, it can be re-built from the log. Each one stores 1TB of message IDs on disk.`,
    },
    {
      title: `Fast-paths with Bloom Filters`,
      description: `Most new messages won’t be duplicated at all. RocksDB uses Bloom Filters to ensure that most IDs don’t have to load from disk.`,
    },
  ],
  [SectionWithAccordion.GDPR_SUPPRESSION]: [
    {
      title: `Centralized user storage in Cerebro`,
      description: `An internal service called Cerebro manages all of the state machines and cleanup work around user suppression and deletion. It’s backed by Kafka and MySQL, replicated 3 times to ensure data ia actually deleted.`,
    },
    {
      title: `Receipts logged in audit database`,
      description: `For every deletion and suppression request logged, we store the receipt in an audit database. It helps provide peace of mind (and compliance) that your data has actually been deleted.`,
    },
    {
      title: `Reliable fanout via ctlstore`,
      description: `Each userId is automatically replicated to a local sqlite instance attached to the worker. It ensures that lookups are fast and reliable. <a href="https://rbranson.medium.com/sharing-sqlite-databases-across-containers-is-surprisingly-brilliant-bacb8d753054">Read more.</a>`,
    },
  ],
  [SectionWithAccordion.IDENTITY_RESOLUTION]: [
    {
      title: `Millisecond resolution in DynamoDB`,
      description: `We’ve partitioned Kafka based upon the ID of each message. Whenever a deduplication worker starts, it first reads the Kafka output topic as the “source of truth” before processing messages.`,
    },
    {
      title: `Configurable with your own custom algorithm`,
      description: `You have your own scheme for managing customers: emails, userIds, device identifiers. customize merging by blocking individual values, limit the number of merged user profiles, or adjust the priority and precedence of individual user IDs.`,
    },
    {
      title: `Deterministic ID matching`,
      description: `We’ve built our algorithms to always be deterministic. Under the hood, <a href="https://segment.com/blog/identity-resolution">we create a graph of identities and a separate merge table</a> so that you can always trace how an identity was merged, and then unmerge it down the road.`,
    },
  ],
  [SectionWithAccordion.PROFILE_API]: [
    {
      title: `300ms response time`,
      description: `We’ve invested heavily in our infrastructure to ensure that 99% of queries are completed in <300ms`,
    },
    {
      title: `Serving millions of requests per day`,
      description: `Our Profile API has scaled to meet the personalization requirements of some of the biggest brands in the world. It currently handles millions of requests per day.`,
    },
    {
      title: `Pull a full user history in JSON`,
      description: `Getting the right data to personalize can be difficult. There’s lots of cryptic variables for individual audiences, which aren’t fully tied together. We’ve made it easy by passing back the events as JSON. They’re easy to reason about and fit with your overall tracking plan.`,
    },
  ],
  [SectionWithAccordion.REVERSE_ETL]: [
    {
      title: `Sync on your schedule`,
      description: `Whether you’re sending data to Salesforce, Braze, or any other destination - manage compute costs and ensure teams have access to timely data.`,
    },
    {
      title: `Enrich and build unified profiles`,
      description: `Send data from your warehouse to the Segment destination where it can be merged into identity-resolved profiles that can be synced back to the warehouse.`,
    },
    {
      title: `Activate data everywhere`,
      description: `With access to 450+ destinations, Segment customers can send data from the warehouse down to any supported destination.`,
    },
  ],
  [SectionWithAccordion.AUDIENCE_CREATION]: [
    {
      title: `Real-time computations with Flink`,
      description: `We’ve built a custom Flink pipeline to ensure that users get routed with affinity, and quickly. Our pipeline can re-compute membership in audiences in real-time.`,
    },
    {
      title: `Historical computations with BigQuery`,
      description: `To re-build historical audiences, we leverage BigQuery. <a href="https://www.youtube.com/watch?v=rECHcoGIJNU">We’ve created wide tables that allow us to run these audience re-computes over terabytes of data on the fly.</a>`,
    },
    {
      title: `AST to SQL`,
      description: `We’ve built an intuitive, simple UI, to create audiences. Under the hood, we parse this into an AST which allows users to dynamically update their audiences with complex aggregations.`,
    },
  ],
  [SectionWithAccordion.CENTRIFUGE]: [
    {
      title: `Fast path wins`,
      description: `Events which fail once are 10x more likely to fail on a retry. Centrifuge prioritizes real-time events to ensure that failing events don’t back up the queue.`,
    },
    {
      title: `Kafka as the replay log`,
      description: `Kafka acts as the backbone for Centrifuge, persisting data across availability zones and ensuring that data will never be lost.`,
    },
    {
      title: `Retry dynamically with flow control`,
      description: `We retry data over a four hour period with exponential backoff. Any data which is not delivered is archived on S3.`,
    },
    {
      title: `Receipts for delivery and failure`,
      description: `Any time one of your events is rejected, we’ll log the error and the message. No need to plumb through error logs, the full request/response payload will show up in the Segment app.`,
    },
  ],
  [SectionWithAccordion.DESTINATIONS]: [
    {
      title: `Surface human readable errors`,
      description: `Not all APIs are created equal. SOAP, XML, cryptic error codes and invalid JSON messages are often par for the course.`,
    },
    {
      title: `Tested across hundreds of APIs and data formats`,
      description: `Each day, our integrations pipeline processes data from 25,000+ companies sending to ${process.env.INTEGRATION_COUNT}+ integrations. we automatically map every variation of “User Signed Up” “Signed Up” “signup” and “user_signup” so you don’t have to.`,
    },
    {
      title: `Per-event visibility`,
      description: `Any time one of your events is rejected, we’ll log the error and the message. No need to plumb through error logs, the full request/response payload will show up in the Segment app.`,
    },
  ],
  [SectionWithAccordion.WAREHOUSES_ETL]: [
    {
      title: `Syncing at scale`,
      description: `Our warehouses pipeline syncs 6T rows each month across thousands of Snowflake, BigQuery, and Redshift warehouses.`,
    },
    {
      title: `Advanced Sync controls`,
      description: `Our customizable scheduler ensures that loading queries don’t block your analysis team. You can schedule at off-peak hours, or run overnight.`,
    },
    {
      title: `Multi-step failure recovery`,
      description: `We run each part of our pipeline as an individual container job. Even if the last load step fails, we can re-run from the transformed dataset saving hours of sync time.`,
    },
    {
      title: `Type conversion`,
      description: `We’ll take free-form JSON and automatically convert it into whichever database types make the most sense pulled from our schema registry.`,
    },
    {
      title: `Schema management`,
      description: `A warehouse is only as useful as the ability for analysts to query it. We materialize views of users and objects so that it is easy to join together data as part of a query and intuitive for analysts.`,
    },
  ],
}

export default accordionData
